/**
 * Shared bits for the Performance Management System screens.
 */
import React from 'react';

export const BAND_STYLE = {
  A: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  B: 'bg-teal-100 text-teal-800 border-teal-200',
  C: 'bg-sky-100 text-sky-800 border-sky-200',
  D: 'bg-amber-100 text-amber-800 border-amber-200',
  E: 'bg-red-100 text-red-800 border-red-200',
};

export const STATUS_STYLE = {
  goals_pending: 'bg-slate-100 text-slate-600',
  goals_set: 'bg-slate-100 text-slate-700',
  self_submitted: 'bg-sky-100 text-sky-700',
  manager_submitted: 'bg-amber-100 text-amber-800',
  approved: 'bg-teal-100 text-teal-800',
  published: 'bg-emerald-100 text-emerald-800',
};

export const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const Badge = ({ children, className = '', testid }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border border-transparent ${className}`}
  >
    {children}
  </span>
);

export const ScoreRing = ({ value = 0, band = '', size = 68 }) => {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const stroke = 6;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const colors = { A: '#059669', B: '#0d9488', C: '#0284c7', D: '#d97706', E: '#dc2626' };
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="#e2e8f0" strokeWidth={stroke} fill="none" />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={colors[band] || '#0d9488'} strokeWidth={stroke} strokeLinecap="round"
        strokeDasharray={`${(c * pct) / 100} ${c}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dasharray 600ms ease' }}
      />
      <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle"
        className="fill-slate-900 font-semibold" style={{ fontSize: size / 3.4 }}>
        {pct.toFixed(0)}
      </text>
    </svg>
  );
};

export const ProgressBar = ({ pct, tone = 'teal' }) => {
  const v = pct === null || pct === undefined ? 0 : Math.max(0, Math.min(120, Number(pct)));
  const tones = {
    teal: 'bg-teal-500', amber: 'bg-amber-500', red: 'bg-red-500', emerald: 'bg-emerald-500',
  };
  const color = v >= 100 ? tones.emerald : v >= 75 ? tones.teal : v >= 50 ? tones.amber : tones.red;
  return (
    <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: `${Math.min(100, (v / 120) * 100)}%`, transition: 'width 500ms ease' }}
      />
    </div>
  );
};

export const RatingPicker = ({ value, onChange, disabled, testid }) => (
  <div className="flex gap-1" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => {
      const active = Number(value) === n;
      return (
        <button
          key={n}
          type="button"
          disabled={disabled}
          onClick={() => onChange(n)}
          data-testid={testid ? `${testid}-${n}` : undefined}
          className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-colors ${
            active
              ? 'bg-teal-600 text-white border-teal-600'
              : 'bg-white text-slate-600 border-slate-300 hover:border-teal-400'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {n}
        </button>
      );
    })}
  </div>
);

export const fmtMetric = (value, unit) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  const txt = Number.isFinite(n) ? `${Number(n.toFixed(2))}` : String(value);
  if (unit === '%') return `${txt}%`;
  if (unit === '/5') return `${txt}/5`;
  return unit ? `${txt} ${unit}` : txt;
};
